import React from "react";
import { GiGearStickPattern } from "react-icons/gi";
import { RiGasStationFill } from "react-icons/ri";
import { PiDiamondsFour } from "react-icons/pi";
import AppCopy from "copy/AppCopy";
import { CarStatusType } from "types/loadedData";
import { useNavigate } from "react-router-dom";
import { Paths } from "router/Paths";
import { CarCardProps } from "declarations/card";
import { useDispatch } from "react-redux";
import { setCar } from "reducers/carReducer";
import { Capitalize, getPrice } from "functions/text";

function CarCard({ car }: CarCardProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const status = car.status;

  const statusTagColor = (carStatus: CarStatusType): string => {
    switch (carStatus) {
      case "Assigned":
        return ClassName.statusTagAssigned;
      case "Cancelled":
        return ClassName.statusTagCancelled;
      case "Rented":
        return ClassName.statusTagRented;
      case "Sold":
        return ClassName.statusTagSold;
      default:
        return ClassName.statusTagActive;
    }
  };

  const statusBorderColor = (carStatus: CarStatusType): string => {
    switch (carStatus) {
      case "Assigned":
        return ClassName.statusContainerAssigned;
      case "Cancelled":
        return ClassName.statusContainerCancelled;
      case "Rented":
        return ClassName.statusContainerRented;
      case "Sold":
        return ClassName.statusContainerSold;
      default:
        return ClassName.statusContainerActive;
    }
  };

  const handleImagePress = () => {
    dispatch(setCar(car));
    navigate(`${Paths.cars}/${car._id}`);
  };

  const { carOwnerText, postedByText, statusText } = AppCopy.carCard;

  return (
    <div className={ClassName.container}>
      <img
        src={car.images?.[0] || ""}
        alt={`${car.model || "Car"} listing`}
        className={ClassName.image}
        onClick={handleImagePress}
      />
      <div className={ClassName.contentContainer}>
        <span className={ClassName.id}>
          {car.tag}
          <strong>{car.listingType === "Rent" ? "R" : "S"}</strong>
        </span>
        {/* Mobile Only div */}
        <div className={ClassName.mobile}>
          <span className={ClassName.model}>{car.model}</span>
          <div className={ClassName.status}>
            <div
              className={`${ClassName.statusTagContainer} ${statusBorderColor(
                status
              )}`}
            >
              <div
                className={`${ClassName.statusTag} ${statusTagColor(status)}`}
              ></div>
            </div>
            <span>{status}</span>
          </div>
        </div>
        <div className={ClassName.content}>
          <div className={ClassName.priceContainer}>
            <span className={ClassName.price}>{getPrice(car.price)}</span>
            <span className={ClassName.model}>{car.model}</span>
            <div className={ClassName.tagContainer}>
              <GiGearStickPattern size={14} className={ClassName.tagIcon} />
              <span>{Capitalize(car.transmissionType)}</span>
            </div>
            <div className={ClassName.tagContainer}>
              <RiGasStationFill size={14} className={ClassName.tagIcon} />
              <span>{Capitalize(car.fuelType)}</span>
            </div>
            <div className={ClassName.tagContainer}>
              <PiDiamondsFour size={14} className={ClassName.tagIcon} />
              <span>{Capitalize(car.usageType)}</span>
            </div>
          </div>
          <div className={ClassName.descriptionContainer}>
            <p className={ClassName.description}>{car.description}</p>
            <div className={ClassName.statusContainer}>
              <div className={ClassName.statusSegment}>
                <span className={ClassName.statusSegmentTitle}>
                  {postedByText}
                </span>
                <span>{car.owner?.name || "Unknown"}</span>
                <span>{car.owner?.phoneNumber || "No phone"}</span>
              </div>
              <div className={ClassName.statusSegment}>
                <span className={ClassName.statusSegmentTitle}>
                  {carOwnerText}
                </span>
                <span>{car.phone || "Not provided"}</span>
              </div>
              <div className={ClassName.statusSegment}>
                <span className={ClassName.statusSegmentTitle}>
                  {statusText}
                </span>
                <div className={ClassName.status}>
                  <div
                    className={`${
                      ClassName.statusTagContainer
                    } ${statusBorderColor(status)}`}
                  >
                    <div
                      className={`${ClassName.statusTag} ${statusTagColor(
                        status
                      )}`}
                    ></div>
                  </div>
                  <span>{status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CarCard;

const ClassName = {
  container:
    "my-1 flex w-full flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2 text-primaryText transition-colors hover:bg-slate-50 sm:my-2 sm:flex-row sm:gap-6 sm:p-4",
  image:
    "h-48 w-full cursor-pointer rounded-lg bg-slate-100 object-cover sm:w-60 md:h-56 md:w-72",
  contentContainer: "flex flex-col flex-1",
  id: "text-sm sm:text-xl mb sm:mb-4 font-bold sm:font-normal",
  content: "hidden sm:flex flex-col lg:flex-row flex-1 gap-4",
  priceContainer: "flex flex-col w-full lg:w-80",
  descriptionContainer: "flex flex-col flex-1",
  price: "text-primary font-bold text-lg",
  model: "text-sm sm:text-base font-semibold mb sm:mb-4",
  tagContainer: "flex items-center gap-2 font-medium text-sm mb-2",
  tagIcon: "text-primary",
  description: "mb-4 line-clamp-3 text-sm leading-6",

  mobile: "flex sm:hidden flex-col",

  statusContainer:
    "flex flex-1 justify-between gap-4 text-secondaryText sm:text-primaryText text-sm font-medium",
  statusSegment: "flex flex-col gap-1 flex-1 ",
  statusSegmentTitle: "text-primary mb-2",

  status: "flex items-center gap-2",
  statusTagContainer:
    "w-4 h-4 rounded-full flex items-center justify-center border",
  statusTag: "w-3 h-3 rounded-full",

  statusContainerAssigned: "border-statusAssigned",
  statusContainerCancelled: "border-statusCancelled",
  statusContainerRented: "border-statusRented",
  statusContainerSold: "border-statusSold",
  statusContainerActive: "border-neutral",

  statusTagAssigned: "bg-statusAssigned",
  statusTagCancelled: "bg-statusCancelled",
  statusTagRented: "bg-statusRented",
  statusTagSold: "bg-statusSold",
  statusTagActive: "bg-neutral",
};
